// Application state manager - integrates user preferences and UI state

import { createUserPreferences, createUIState } from "./index";
import { createPathStore } from "./path-store";
import { screenStatePath } from "./path-helpers";
// Selection schemas are hydrated via adapters; no direct use here currently
// import { zSelectedMode, zSelectedTool } from "./schemas";
import { createSelectionController } from "./selection-controller";
import { createBehaviorDetector } from "./ai-behavior-detector";
import { createKeyboardManager } from "../utils/keyboard-manager";
import { updateProjectPrefs, updateUserPrefs } from "../api";
import type {
  UserPreferences,
  UserPreferencesAPI,
  UIStateAPI,
  AppContext,
} from "../types";
import { createScreenRegistry } from "./state-sync/adapters/screen";
import {
  joinProjectRoom,
  leaveProjectRoom,
  wireProjectPrefsDelta,
  joinUserRoom,
  leaveUserRoom,
  wireUserPrefsDelta,
} from "./state-sync/core/socket-room";
import type { BehaviorDetectorAPI } from "./ai-behavior-detector";
import type { KeyboardManagerAPI } from "../utils/keyboard-manager";
import { createPersistor } from "./state-sync/core/persist";
import {
  extractPersistedPrefsFromScreen,
  extractUiStatePatchFromScreen,
  resolveProjectIdFromSessionNode,
} from "./state-sync/core/screen-state";
import {
  createHandednessState,
  type HandednessStateAPI,
} from "@/state/handedness";

export interface AppStateManagerConfig {
  readonly onUserPreferencesChange?: (preferences: any) => void;
  readonly onUIStateChange?: (state: any) => void;
  readonly autoSavePreferences?: boolean;
  readonly autoSaveUIState?: boolean;
  readonly storageKey?: string;
  readonly uiStateStorageKey?: string;
}

export interface AppStateManagerAPI {
  readonly userPreferences: UserPreferencesAPI;
  readonly uiState: UIStateAPI;
  readonly store: ReturnType<typeof createPathStore>;
  readonly behaviorDetector: BehaviorDetectorAPI;
  readonly keyboardManager: KeyboardManagerAPI;
  readonly appContext: AppContext;
  readonly applyTheme: () => void;
  readonly syncWithStage: (stageAPI: any) => void;
  readonly getCurrentProjectId: () => string | null;
  readonly getScreenStatePath: (key: string) => string | null;
  readonly handedness: HandednessStateAPI;
  readonly getSocketId: () => string | null;
  readonly getSourceId: () => string;
  readonly updateUserPreferences: (
    updates: Partial<UserPreferences["user"]>,
    options?: { source?: string; persist?: boolean }
  ) => Promise<void>;
  readonly destroy: () => void;
}

/**
 * Creates an application state manager that coordinates user preferences and UI state
 *
 * @param appContext - Global application context
 * @param config - Configuration options
 * @returns App state manager API
 *
 * @example
 * ```typescript
 * const appStateManager = createAppStateManager(appContext, {
 *   autoSavePreferences: true,
 *   onUserPreferencesChange: (prefs) => applyTheme(prefs.user.theme)
 * });
 *
 * // Use the state managers
 * appStateManager.userPreferences.updateUser({ theme: 'dark' });
 * appStateManager.uiState.setTool('draw', 'ballpoint');
 *
 * appStateManager.destroy(); // Always call when done
 * ```
 */
export const createAppStateManager = (
  appContext: AppContext,
  config: AppStateManagerConfig = {}
): AppStateManagerAPI => {
  const {
    onUserPreferencesChange,
    onUIStateChange,
    autoSavePreferences = true,
    autoSaveUIState = true,
    storageKey = "sketch-app-preferences",
    uiStateStorageKey = "sketch-app-ui-state",
  } = config;

  // Create user preferences manager
  const userPreferences = createUserPreferences({
    autoSave: autoSavePreferences,
    storageKey,
    onUpdate: (preferences) => {
      onUserPreferencesChange?.(preferences);
      applyTheme();
    },
  });

  const sourceId: string = (() => {
    try {
      const key = "ui:sourceId";
      const existing = sessionStorage.getItem(key);
      if (existing) return existing;
      const id =
        globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
      sessionStorage.setItem(key, id);
      return id;
    } catch {
      return `${Date.now()}-${Math.random()}`;
    }
  })();

  const shouldPersistUserPrefs = (source?: string): boolean => {
    if (!source) return true;
    return !["session", "socket", "hydration", "store", "init"].includes(
      source
    );
  };

  const sanitizeUserPreferenceUpdates = (
    updates: Partial<UserPreferences["user"]>
  ): Partial<UserPreferences["user"]> => {
    const sanitized: Partial<UserPreferences["user"]> = {};
    for (const key of Object.keys(updates) as (
      | keyof UserPreferences["user"]
      | string
    )[]) {
      const value = (updates as any)[key];
      if (value !== undefined) {
        (sanitized as any)[key] = value;
      }
    }
    return sanitized;
  };

  const mapUserPrefsForServer = (
    updates: Partial<UserPreferences["user"]>
  ): Record<string, unknown> => {
    const server: Record<string, unknown> = {};
    if (Object.prototype.hasOwnProperty.call(updates, "name"))
      server.name = updates.name;
    if (Object.prototype.hasOwnProperty.call(updates, "language"))
      server.language = updates.language;
    if (Object.prototype.hasOwnProperty.call(updates, "theme"))
      server.theme = updates.theme;
    if (Object.prototype.hasOwnProperty.call(updates, "dominant_hand"))
      server.dominantHand = updates.dominant_hand;
    if (Object.prototype.hasOwnProperty.call(updates, "drag_behavior"))
      server.dragBehavior = updates.drag_behavior;
    if (Object.prototype.hasOwnProperty.call(updates, "assistant"))
      server.assistant = updates.assistant;
    if (Object.prototype.hasOwnProperty.call(updates, "tool_preferences"))
      server.toolPreferences = updates.tool_preferences;
    if (Object.prototype.hasOwnProperty.call(updates, "memories_for_llm"))
      server.memoriesForLLM = (updates as any).memories_for_llm;
    if (Object.prototype.hasOwnProperty.call(updates, "auth"))
      server.auth = updates.auth;
    return server;
  };

  const USER_PREF_OVERRIDES: Record<string, keyof UserPreferences["user"]> = {
    dominantHand: "dominant_hand",
    handedness: "dominant_hand",
    toolPreferences: "tool_preferences",
    dragBehavior: "drag_behavior",
    memoriesForLLM: "memories_for_llm",
  };

  const USER_PREF_VALUE_TRANSFORMS: Partial<
    Record<keyof UserPreferences["user"], (value: unknown) => unknown>
  > = {
    theme: (value) =>
      value === "light" || value === "dark" ? value : undefined,
    dominant_hand: (value) =>
      value === "left" || value === "right" ? value : undefined,
  };

  const camelToSnake = (key: string): string =>
    key.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toLowerCase();

  const resolveUserPrefKey = (
    key: string,
    current: UserPreferences["user"]
  ): keyof UserPreferences["user"] | null => {
    if (Object.prototype.hasOwnProperty.call(current, key)) {
      return key as keyof UserPreferences["user"];
    }
    const override = USER_PREF_OVERRIDES[key];
    if (override) return override;
    const snakeKey = camelToSnake(key);
    if (Object.prototype.hasOwnProperty.call(current, snakeKey)) {
      return snakeKey as keyof UserPreferences["user"];
    }
    return null;
  };

  const updateUserPreferences = async (
    updates: Partial<UserPreferences["user"]>,
    options: { source?: string; persist?: boolean } = {}
  ): Promise<void> => {
    const sanitized = sanitizeUserPreferenceUpdates(updates);
    if (!Object.keys(sanitized).length) return;

    userPreferences.updateUser(sanitized);

    const shouldPersist =
      options.persist !== undefined
        ? options.persist
        : shouldPersistUserPrefs(options.source);
    if (!shouldPersist) return;

    const payload = mapUserPrefsForServer(sanitized);
    if (!Object.keys(payload).length) return;

    try {
      await updateUserPrefs({
        ...payload,
        sourceId,
      });
    } catch (error) {
      console.warn("Failed to update user preferences", error);
    }
  };

  // Create UI state manager
  let uiState: UIStateAPI;

  const uiStateConfig: any = {
    onToolChange: (tool: any, subtool: any, modeOverride: any) => {
      // Emit tool change events through app event bus
      const mode = modeOverride ?? uiState?.getState?.()?.selectedMode ?? null;
      appContext.eventBus.emit("tool-changed", { tool, subtool, mode });
      try {
        persistUIState();
      } catch {}
    },
    onSelectionChange: (selection: any) => {
      // Emit selection change events through app event bus
      appContext.eventBus.emit("selection-changed", { selection });
    },
  };

  if (onUIStateChange) {
    uiStateConfig.onStateChange = onUIStateChange;
  }

  // Add localStorage configuration for UI state
  uiStateConfig.autoSave = autoSaveUIState;
  uiStateConfig.storageKey = uiStateStorageKey;

  uiState = createUIState(uiStateConfig);

  // Create path-based store
  const store = createPathStore(appContext);

  // Instantiate selection controller (handles project/page selection + persistence)
  const selectionController = createSelectionController(appContext, uiState);

  // Create AI behavior detector
  const behaviorDetector = createBehaviorDetector(userPreferences, uiState, {
    minConfidence: 0.6,
    maxMemories: 20,
    detectionInterval: 30000,
  });

  const handednessState = createHandednessState({
    eventBus: appContext.eventBus,
    session: (appContext as any)?.session ?? null,
  });

  const unsubscribeHandednessChanged = appContext.eventBus.on<{
    handedness?: "left" | "right";
    source?: string;
  }>("ui:handedness:changed", (payload) => {
    const handedness = payload?.handedness;
    if (handedness !== "left" && handedness !== "right") return;
    if (payload?.source === "socket") return;
    void updateUserPreferences(
      { dominant_hand: handedness } as Partial<UserPreferences["user"]>,
      payload?.source ? { source: payload.source } : {}
    );
  });

  const applyUserPrefsPatch = (patch: Record<string, unknown>) => {
    if (!patch || typeof patch !== "object") return;

    const currentPrefs = userPreferences.getPreferences().user;
    const updates: Partial<UserPreferences["user"]> = {};
    const currentPrefsRecord = currentPrefs as Record<string, unknown>;

    for (const [key, rawValue] of Object.entries(patch)) {
      const target = resolveUserPrefKey(key, currentPrefs);
      if (!target) continue;

      const transformer = USER_PREF_VALUE_TRANSFORMS[target];
      const value = transformer ? transformer(rawValue) : rawValue;
      if (value === undefined) continue;

      if (
        Object.prototype.hasOwnProperty.call(currentPrefsRecord, target) &&
        currentPrefsRecord[target] === value
      ) {
        continue;
      }

      (updates as any)[target] = value;
    }

    const dominantHandUpdate = updates.dominant_hand;

    if (Object.keys(updates).length) {
      void updateUserPreferences(updates, { source: "socket" });
    }

    if (dominantHandUpdate === "left" || dominantHandUpdate === "right") {
      if (dominantHandUpdate !== currentPrefs.dominant_hand) {
        handednessState.set(dominantHandUpdate, { source: "socket" });
      }
    }
  };

  // Socket realtime wiring
  let socket: import("socket.io-client").Socket | null = null;
  let currentSocketId: string | null = null;
  const ensureSocket = async () => {
    if (socket) return socket;
    try {
      const { connectSocket } = await import("../socket");
      socket = await connectSocket();
      try {
        console.log("[socket] ensureSocket initialized");
      } catch {}
      // Auto-rejoin on reconnect
      try {
        socket.on("connect", () => {
          currentSocketId = socket?.id ?? null;
          try {
            if (currentProjectId) {
              cleanupProjectPrefsDelta?.();
              cleanupProjectPrefsDelta = wireProjectPrefsDelta(socket! as any, {
                store: store as any,
                screenStatePath,
                sourceId,
                getCurrentProjectId: () => currentProjectId,
                normalize: (patch: Record<string, unknown>, pid: string) => {
                  try {
                    const {
                      normalizeSelectionPatch,
                    } = require("./state-sync/adapters/screen/selection.adapter");
                    const normalized = normalizeSelectionPatch(
                      patch,
                      screenStatePath,
                      pid
                    );
                    const nextPatches = Array.isArray(normalized)
                      ? [...normalized]
                      : [];
                    if (typeof (patch as any).currentSketchId === "string") {
                      nextPatches.push({
                        path: screenStatePath(pid, "currentSketchId"),
                        value: (patch as any).currentSketchId,
                      });
                    }
                    return nextPatches;
                  } catch {
                    return [];
                  }
                },
              });
              socket!.emit("project:join", { projectId: currentProjectId });
            }

            if (currentUserId) {
              try {
                joinUserRoom(socket!, currentUserId);
              } catch {}
            }

            cleanupUserPrefsDelta?.();
            cleanupUserPrefsDelta = wireUserPrefsDelta(socket!, {
              sourceId,
              onPatch: (patch) => applyUserPrefsPatch(patch),
            });
          } catch {}
        });
      } catch {}
      try {
        socket.on("disconnect", () => {
          currentSocketId = null;
        });
      } catch {}
      try {
        socket.on("sketch:strokes:patched", (payload: any) => {
          appContext.eventBus.emit("sketch:strokes:patched", payload);
        });
      } catch {}
      try {
        socket.on("sketch:patched", (payload: any) => {
          appContext.eventBus.emit("sketch:patched", payload);
        });
      } catch {}
      try {
        socket.on("sketch:updated", (payload: any) => {
          appContext.eventBus.emit("sketch-updated", payload);
        });
      } catch {}
      return socket;
    } catch (e) {
      console.warn("Failed to initialize socket", e);
      return null as any;
    }
  };
  // Kick off socket connection early (room join happens when we know projectId)
  (async () => {
    try {
      await ensureSocket();
    } catch {}
  })();

  // Track current project and user ids for realtime updates
  let currentProjectId: string | null = null;
  let currentUserId: string | null = null;
  let cleanupUserPrefsDelta: (() => void) | null = null;
  let cleanupProjectPrefsDelta: (() => void) | null = null;
  let suppressPersistence = false;
  let suppressTimer: any = null;
  let isPanning = false; // Track stage pan/drag state to suppress persistence during drag

  // Track last persisted subset moved to persistor core
  const beginHydrationWindow = (ms: number = 800) => {
    suppressPersistence = true;
    if (suppressTimer) clearTimeout(suppressTimer);
    suppressTimer = setTimeout(() => {
      suppressPersistence = false;
      suppressTimer = null;
    }, ms);
  };

  // Persistor
  const persistor = createPersistor({
    uiState,
    getCurrentProjectId: () => currentProjectId,
    sourceId,
    debounceMs: 300,
  });
  const persistUIState = () => {
    if (suppressPersistence || isPanning) return;
    persistor.persist();
  };

  // UIState → Store bridging is handled by dedicated adapters now

  // Store -> UIState for audio/theme moved to adapters

  // Store -> UIState for viewport moved to viewport adapter

  // Store -> UIState for helpers moved to helpers adapter

  // Adapter registry and wiring
  const adapterDeps = {
    store,
    uiState,
    eventBus: appContext.eventBus,
    getPath: (key: string) =>
      currentProjectId ? screenStatePath(currentProjectId, key) : null,
    persist: persistUIState,
    handedness: handednessState,
  } as const;
  const registry = createScreenRegistry(adapterDeps as any);

  // Language and breakpoint subscribers moved to adapters

  // Persistence is handled by adapters with source checking (no blanket ui-state-changed persistence)
  // Adapters call persist() only when source !== "socket" to avoid feedback loops

  const unsubscribeSketchIdFromStore = store.subscribe(
    (path: string) => path.endsWith(".screenState.currentSketchId"),
    ({ value, source }) => {
      if (source !== "socket") return;
      if (typeof value !== "string" || !value) return;
      appContext.eventBus.emit("sketch-selected", { sketchId: value });
    }
  );

  // Subscribe to page changes to persist currentPage
  const unsubscribePageChanged = appContext.eventBus.on(
    "page-changed",
    (data: any) => {
      if (suppressPersistence) return;
      if (!currentProjectId) return;
      const pageId = data?.pageId;
      if (!pageId) return;
      updateProjectPrefs(currentProjectId, {
        currentPage: pageId,
        sourceId,
      }).catch(() => {});
    }
  );

  const unsubscribeSketchChanged = appContext.eventBus.on(
    "sketch-changed",
    (data: any) => {
      if (suppressPersistence) return;
      const sketchId = data?.sketchId;
      if (!sketchId) return;
      const projectId = currentProjectId ?? data?.projectId;
      if (!projectId) return;
      updateProjectPrefs(projectId, {
        currentSketchId: sketchId,
        sourceId,
      }).catch(() => {});
    }
  );

  // Sync from server session when available
  const handleSessionUpdated = (payload: any) => {
      const session = payload?.session;
      if (!session) {
        (async () => {
          try {
            const s = await ensureSocket();
            if (!s) return;
            if (currentUserId) {
              try {
                leaveUserRoom(s as any, currentUserId);
              } catch {}
            }
          } catch {}
        })();
        currentUserId = null;
        cleanupUserPrefsDelta?.();
        cleanupUserPrefsDelta = null;
        return;
      }

      beginHydrationWindow(800);
      try {
        // Map user prefs
        const prefs = session.userPrefs || {};
        const sessionUserUpdates: Partial<UserPreferences["user"]> = {
          name: session.user?.name,
          language: prefs.language,
          dominant_hand: prefs.dominantHand,
          theme: prefs.theme,
          drag_behavior: prefs.dragBehavior,
          assistant: prefs.assistant,
          tool_preferences: prefs.toolPreferences,
        };
        void updateUserPreferences(sessionUserUpdates, {
          source: "session",
          persist: false,
        });

        // Map UI state
        const rawScreen = session.currentProject?.screenState;
        const prevProjectId = currentProjectId;
        const prevUserId = currentUserId;
        const nextUserIdCandidate =
          typeof session.user?.id === "string"
            ? session.user.id
            : typeof (session.user as any)?.userId === "string"
            ? (session.user as any).userId
            : typeof (session.user as any)?._id === "string"
            ? (session.user as any)._id
            : null;
        currentUserId = nextUserIdCandidate;
        const currentProjectNode = (session.currentProject as any) ?? null;
        currentProjectId = resolveProjectIdFromSessionNode(
          currentProjectNode,
          rawScreen as any
        );
        if (rawScreen) {
          const screenPatch = extractUiStatePatchFromScreen(rawScreen as any);
          uiState.applyState(screenPatch);
          const desiredMode =
            (screenPatch as any)?.selectedMode ??
            ((rawScreen as Record<string, unknown>)?.selectedMode as
              | string
              | undefined) ??
            ((rawScreen as Record<string, unknown>)?.selected_mode as
              | string
              | undefined);
          const desiredSubtool =
            (screenPatch as any)?.selectedTool ??
            (screenPatch as any)?.selected_subtool ??
            ((rawScreen as Record<string, unknown>)?.selectedTool as
              | string
              | undefined) ??
            ((rawScreen as Record<string, unknown>)?.selected_subtool as
              | string
              | undefined);

          if (desiredMode) {
            try {
              uiState.setMode(desiredMode as any);
            } catch (error) {
              console.warn("Failed to apply session mode", error);
            }
          }

          if (desiredSubtool) {
            try {
              uiState.setToolSub(desiredSubtool as any);
            } catch (error) {
              console.warn("Failed to apply session tool", error);
            }
          }

          // Theme, i18n, breakpoint, viewport, helpers, audio will be hydrated via adapters below

          // selectedMode/selectedTool are hydrated into the store below; UI state reflects via store subscribers

          // Seed last persisted snapshot from server-provided values
          const persistedSnapshot = extractPersistedPrefsFromScreen(
            rawScreen as any
          );
          persistor.seedSnapshot(persistedSnapshot);

          if (currentProjectId) {
            // Hydrate via adapters (single hydration window)
            store.withHydrationWindow(800, () => {
              registry.hydrateFromSession(rawScreen as any);
            });
          }
        }

        // Join project room for realtime and listen for prefs deltas
        (async () => {
          try {
            const s = await ensureSocket();
            if (!s) return;
            if (prevProjectId && prevProjectId !== currentProjectId) {
              try {
                leaveProjectRoom(s as any, prevProjectId);
              } catch {}
            }
            if (currentProjectId) {
              cleanupProjectPrefsDelta?.();
              cleanupProjectPrefsDelta = wireProjectPrefsDelta(s as any, {
                store: store as any,
                screenStatePath,
                sourceId,
                getCurrentProjectId: () => currentProjectId,
                normalize: (patch: Record<string, unknown>, pid: string) => {
                  // Example: selection normalization via adapter helper
                  try {
                    const {
                      normalizeSelectionPatch,
                    } = require("./state-sync/adapters/screen/selection.adapter");
                    const normalized = normalizeSelectionPatch(
                      patch,
                      screenStatePath,
                      pid
                    );
                    const nextPatches = Array.isArray(normalized)
                      ? [...normalized]
                      : [];
                    if (typeof (patch as any).currentSketchId === "string") {
                      nextPatches.push({
                        path: screenStatePath(pid, "currentSketchId"),
                        value: (patch as any).currentSketchId,
                      });
                    }
                    return nextPatches;
                  } catch {
                    return [];
                  }
                },
              });
              joinProjectRoom(s as any, currentProjectId);
            }
            if (prevUserId && prevUserId !== currentUserId) {
              try {
                leaveUserRoom(s as any, prevUserId);
              } catch {}
            }
            if (currentUserId) {
              joinUserRoom(s as any, currentUserId);
            }
            cleanupUserPrefsDelta?.();
            cleanupUserPrefsDelta = wireUserPrefsDelta(s as any, {
              sourceId,
              onPatch: (patch) => applyUserPrefsPatch(patch),
            });
          } catch {}
        })();
      } catch (e) {
        console.warn("Failed to apply session to app state", e);
      }
  };

  const unsubscribeSession = appContext.eventBus.on(
    "session:updated",
    handleSessionUpdated
  );

  // Seed state from an existing session if it was set before listeners attached.
  try {
    const existingSession = (appContext as any)?.session?.getSession?.();
    if (existingSession) {
      handleSessionUpdated({ session: existingSession });
    }
  } catch {}

  // Also capture project id from project-changed events
  const unsubscribeProjectChanged = appContext.eventBus.on(
    "project-changed",
    (data: any) => {
      if (data?.projectId) currentProjectId = data.projectId;
    }
  );

  const unsubscribeShowAllChanged = appContext.eventBus.on(
    "show-all-changed",
    (payload: any) => {
      if (payload?.source === "socket") return;
      persistUIState();
    }
  );

  // Track stage panning to suppress persistence during drag
  const unsubscribePanStart = appContext.eventBus.on("stage-pan-start", () => {
    isPanning = true;
  });

  const unsubscribePanEnd = appContext.eventBus.on("stage-pan-end", () => {
    isPanning = false;
    // Persist once after pan ends
    if (!suppressPersistence) {
      persistUIState();
    }
  });

  // Create keyboard manager with modifier state integration
  const keyboardManager = createKeyboardManager({
    onModifierChange: (modifiers) => {
      // Update UI state when physical keyboard modifiers change
      uiState.setShiftPressed(modifiers.shift);
      uiState.setAltPressed(modifiers.alt);

      console.log("🎹 Physical keyboard modifiers changed:", modifiers);
    },
    shortcuts: [
      // Basic shortcuts - more can be added later
      {
        key: "Escape",
        description: "Cancel current action",
        handler: () => {
          console.log("🎹 Escape pressed - canceling action");
          // Let other components handle escape by not preventing default
          return false;
        },
      },
      {
        key: "z",
        modifiers: { meta: true },
        description: "Undo",
        handler: () => {
          appContext.eventBus.emit("undo-requested", { source: "keyboard" });
        },
      },
      {
        key: "z",
        modifiers: { ctrl: true },
        description: "Undo",
        handler: () => {
          appContext.eventBus.emit("undo-requested", { source: "keyboard" });
        },
      },
      {
        key: "z",
        modifiers: { meta: true, shift: true },
        description: "Redo",
        handler: () => {
          appContext.eventBus.emit("redo-requested", { source: "keyboard" });
        },
      },
      {
        key: "z",
        modifiers: { ctrl: true, shift: true },
        description: "Redo",
        handler: () => {
          appContext.eventBus.emit("redo-requested", { source: "keyboard" });
        },
      },
    ],
  });

  // Apply global app theme based ONLY on user preferences (not UI state)
  function applyTheme() {
    const prefs = userPreferences.getPreferences();
    const theme = prefs.user.theme;
    const root = document.documentElement;

    if (theme === "dark") {
      root.style.setProperty("--color-surface", "#1a1a1a");
      root.style.setProperty("--color-text", "#ffffff");
      root.style.setProperty("--color-border", "#333333");
      root.style.setProperty("--color-surface--hover", "#2a2a2a");
    } else {
      root.style.setProperty("--color-surface", "#ffffff");
      root.style.setProperty("--color-text", "#000000");
      root.style.setProperty("--color-border", "#e0e0e0");
      root.style.setProperty("--color-surface--hover", "#f5f5f5");
    }

    // Also emit theme change event
    appContext.eventBus.emit("theme-changed", { theme });
  }

  // Sync UI state with Stage component
  const syncWithStage = (stageAPI: any) => {
    if (!stageAPI) return;

    // Listen to stage events and update UI state accordingly
    const unsubscribeTransform = appContext.eventBus.on(
      "stage-transform-changed",
      (data: any) => {
        if (data.scale) {
          uiState.setZoom(data.scale, "custom");
        }
      }
    );

    const unsubscribeModeChange = appContext.eventBus.on(
      "stage-mode-changed",
      (data: any) => {
        // Update UI state based on stage mode changes
        if (data.mode === "edit") {
          uiState.setTool("select");
        }
      }
    );

    // Store cleanup functions for later
    const cleanup = () => {
      unsubscribeTransform();
      unsubscribeModeChange();
    };

    return cleanup;
  };

  // Apply initial theme
  applyTheme();

  // Seed from existing session if already loaded before this manager was constructed
  try {
    const sessMgr = (appContext as any)?.session;
    if (sessMgr && typeof sessMgr.getSession === "function") {
      const current = sessMgr.getSession();
      if (current) {
        beginHydrationWindow(800);
        const prefs = current.userPrefs || {};
        const sessionUserUpdates: Partial<UserPreferences["user"]> = {
          name: current.user?.name,
          language: prefs.language,
          dominant_hand: prefs.dominantHand,
          theme: prefs.theme,
          drag_behavior: prefs.dragBehavior,
          assistant: prefs.assistant,
          tool_preferences: prefs.toolPreferences,
        };
        void updateUserPreferences(sessionUserUpdates, {
          source: "session",
          persist: false,
        });

        const rawScreen = current.currentProject?.screenState;
        const currentProjectNode = (current.currentProject as any) ?? null;
        currentProjectId = resolveProjectIdFromSessionNode(
          currentProjectNode,
          rawScreen as any
        );
        const preloadedUserId =
          typeof current.user?.id === "string"
            ? current.user.id
            : typeof (current.user as any)?.userId === "string"
            ? (current.user as any).userId
            : typeof (current.user as any)?._id === "string"
            ? (current.user as any)._id
            : null;
        currentUserId = preloadedUserId;

        if (rawScreen) {
          const screenPatch = extractUiStatePatchFromScreen(rawScreen as any);
          uiState.applyState(screenPatch);

          if (currentProjectId) {
            // Hydrate via adapters (single hydration window); adapters will reflect Store→UI
            store.withHydrationWindow(800, () => {
              registry.hydrateFromSession(rawScreen as any);
            });
          }

          // Seed last persisted snapshot from server-provided values
          const persistedSnapshot = extractPersistedPrefsFromScreen(
            rawScreen as any
          );
          persistor.seedSnapshot(persistedSnapshot);
        }

        (async () => {
          try {
            const s = await ensureSocket();
            if (!s) return;
            if (currentProjectId) {
              console.log("🔄 Socket: joining project room (boot)", {
                projectId: currentProjectId,
              });
              try {
                s.emit(
                  "project:join",
                  { projectId: currentProjectId },
                  (ack: any) => {
                    try {
                      console.log("✅ project:join ack (boot)", ack);
                    } catch {}
                  }
                );
              } catch {}
              try {
                const room = `project:${currentProjectId}`;
                s.emit("join", room, (ack: any) => {
                  try {
                    console.log("✅ legacy join ack (boot)", { room, ack });
                  } catch {}
                });
              } catch {}
            }
            if (currentUserId) {
              joinUserRoom(s, currentUserId);
            }
            if (cleanupUserPrefsDelta) {
              (cleanupUserPrefsDelta as () => void)();
            }
            cleanupUserPrefsDelta = wireUserPrefsDelta(s, {
              sourceId,
              onPatch: (patch) => applyUserPrefsPatch(patch),
            });
          } catch {}
        })();
      }
    }
  } catch {}

  const destroy = () => {
    cleanupUserPrefsDelta?.();
    cleanupUserPrefsDelta = null;
    if (socket && currentUserId) {
      try {
        leaveUserRoom(socket, currentUserId);
      } catch {}
    }
    userPreferences.destroy();
    uiState.destroy();
    behaviorDetector.destroy();
    keyboardManager.destroy();
    selectionController.destroy();
    handednessState.destroy();
    unsubscribeSession();
    unsubscribePageChanged();
    unsubscribeSketchChanged();
    unsubscribeSketchIdFromStore();
    unsubscribeProjectChanged();
    unsubscribeShowAllChanged();
    unsubscribePanStart();
    unsubscribePanEnd();
    unsubscribeHandednessChanged?.();
    // audio/theme subscribers are owned by adapters
    // viewport and helpers subscribers are owned by their adapters
    // language and breakpoint subscribers are owned by their adapters
    try {
      // Broadcast destroy so adapters self-clean
      appContext.eventBus.emit("app:destroy", {});
    } catch {}
  };

  return {
    userPreferences,
    uiState,
    store,
    behaviorDetector,
    keyboardManager,
    appContext,
    applyTheme,
    syncWithStage,
    getCurrentProjectId: () => currentProjectId,
    getScreenStatePath: (key: string) =>
      currentProjectId ? screenStatePath(currentProjectId, key) : null,
    getSocketId: () => currentSocketId,
    getSourceId: () => sourceId,
    handedness: handednessState,
    updateUserPreferences,
    destroy,
  };
};
